import React, {FC} from 'react'
import {useBoostExpediaBackgroundTabOffer} from 'components/BoostExpedia'
import useRedirectLink from 'hooks/useRedirectLink'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {useEmbeddedLinkHandlerFactory} from '@vio/core/src/_web/utils/embedded/useEmbeddedLinkHandler'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {shouldOfferBeBranded} from '../utils'
import {OfferLinkWrapper} from './styles'

interface Props {
  addNoReferrer: string
  anchorPriceTotal: number
  className: string
  dataId: string
  handleClick: (event: React.MouseEvent<Element, MouseEvent>) => void
  hotelId: string
  inViewRef: (node?: Element) => void
  offer: SearchOffer
  onHover?: () => void
  onContextMenu?: React.MouseEventHandler<HTMLAnchorElement>
  onAuxClick?: React.MouseEventHandler<HTMLAnchorElement>
}

export const OfferLink: FC<Props> = ({
  addNoReferrer,
  anchorPriceTotal,
  children,
  className,
  dataId,
  handleClick,
  hotelId,
  inViewRef,
  offer,
  onHover,
  onContextMenu,
  onAuxClick
}) => {
  const href = useRedirectLink({
    anchorPriceTotal,
    offer,
    hotelId
  })

  const {
    monetisation: {hasBrandedOffers},
    commerce: {bookOnBrand}
  } = useBrandCapabilities()
  const isBookOnBrand = bookOnBrand.enabled
  const isEmbedded = useIsEmbeddedWithBrand()

  const isBrandedDeal = shouldOfferBeBranded({
    offerIntermediaryProviderCode: offer.intermediaryProvider,
    isBookOnBrand,
    hasBrandedOffers
  })

  // If the deal is branded and the page is embedded, set the target to _self as iframes sometimes block navigation to _blank
  const initialTarget = isBrandedDeal && isEmbedded ? '_self' : '_blank'

  const embeddedLinkHandler = useEmbeddedLinkHandlerFactory()
  const {onClick: embeddedOnClick, target: embeddedTarget} =
    embeddedLinkHandler({
      href,
      onClick: handleClick,
      target: initialTarget
    })

  // Added as part of 2a7f5ff0-expedia-back-tab
  const onClick = useBoostExpediaBackgroundTabOffer({
    hotelId,
    offer,
    originalOnClick: embeddedOnClick
  })

  return (
    <OfferLinkWrapper
      ref={inViewRef}
      className={className}
      href={href}
      target={embeddedTarget}
      rel={`noopener ${addNoReferrer}`}
      data-id={dataId}
      onClick={onClick}
      onMouseEnter={onHover}
      onContextMenu={onContextMenu}
      onAuxClick={onAuxClick}
    >
      {children}
    </OfferLinkWrapper>
  )
}
